import _extends from '@babel/runtime/helpers/extends';
import React__default, { createContext, useContext } from 'react';
import { g as getDataTaktAttributesFunction } from './TaktIdContext.utils-B0CzqlN8.js';

const DEFAULT_DELIMITER = ':';
const TaktIdContext = /*#__PURE__*/createContext({
  taktFeatureChain: [],
  delimiter: undefined,
  useFallbacks: true,
  taktPrefix: undefined,
  taktValues: undefined
});

// ---------------------------------------------

const TaktIdProvider = ({
  children,
  taktId,
  customDelimiter_DO_NOT_USE,
  // eslint-disable-line camelcase
  useFallbacks = true,
  fallbackId,
  taktIdPrefix,
  taktValue
}) => {
  var _context$taktFeatureC, _ref, _context$delimiter, _context$taktFeatureC2, _ref2;
  const context = useContext(TaktIdContext);
  /**
   * if this is the top of the feature chain, and no taktId was provided,
   * disregard this provider
   */
  if (!((_context$taktFeatureC = context.taktFeatureChain) !== null && _context$taktFeatureC !== void 0 && _context$taktFeatureC.length) && !taktId) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }
  /**
   * use the top level delimiter if set,
   * else we're at the top level and use the one passed in,
   * else fallback to default ':'
   */
  const delim = (_ref = (_context$delimiter = context.delimiter) !== null && _context$delimiter !== void 0 ? _context$delimiter : customDelimiter_DO_NOT_USE) !== null && _ref !== void 0 ? _ref : DEFAULT_DELIMITER; // eslint-disable-line camelcase, max-len
  // set whether fallbacks should be used based on having an existing context & useFallbacks flag
  const canUseFallbacks = (_context$taktFeatureC2 = context.taktFeatureChain) !== null && _context$taktFeatureC2 !== void 0 && _context$taktFeatureC2.length ? context.useFallbacks : useFallbacks;
  // determine which Id to append to the chain
  const appendedId = taktId || (canUseFallbacks ? fallbackId : undefined);
  // if no id was provided that can be used, do not append anything with this provider
  if (!appendedId) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }

  // append any new taktValues to the stored taktValue object
  const appendedValue = {
    ...context.taktValues,
    ...taktValue
  };

  // replace the prefix if one is passed in
  const updatedPrefix = (_ref2 = taktIdPrefix !== null && taktIdPrefix !== void 0 ? taktIdPrefix : context.taktPrefix) !== null && _ref2 !== void 0 ? _ref2 : undefined;

  // if previous prefix exists, append to it, else use passed in prefix to start context chain
  const appendedChain = [...(context === null || context === void 0 ? void 0 : context.taktFeatureChain), appendedId];
  return /*#__PURE__*/React__default.createElement(TaktIdContext.Provider, {
    value: {
      taktFeatureChain: appendedChain,
      delimiter: delim,
      useFallbacks: canUseFallbacks,
      taktValues: appendedValue,
      taktPrefix: updatedPrefix
    }
  }, children);
};

// --------------------------------------------

const getTaktIdParams = ({
  taktIdContext,
  taktId,
  fallbackId,
  taktIdPrefix,
  taktValue
}) => {
  const {
    taktFeatureChain,
    taktPrefix,
    taktValues,
    delimiter
  } = taktIdContext;

  // determine the taktId to append to use
  // if there is no parent chain, do not append anything or return anything from the context
  const hasParentChain = !!(taktFeatureChain !== null && taktFeatureChain !== void 0 && taktFeatureChain.length);
  const updatedPrefix = hasParentChain ? taktIdPrefix !== null && taktIdPrefix !== void 0 ? taktIdPrefix : taktPrefix : taktIdPrefix;
  const dataTaktId = hasParentChain ? taktId || (taktIdContext.useFallbacks ? fallbackId : undefined) : taktId;
  const dataTaktValue = hasParentChain ? {
    ...taktValues,
    ...taktValue
  } : taktValue;
  return {
    taktIdPrefix: updatedPrefix,
    unprefixedTaktId: dataTaktId,
    dataTaktId: updatedPrefix ? `${updatedPrefix}-${dataTaktId}` : dataTaktId,
    dataTaktValue,
    dataTaktFeature: taktFeatureChain.join(delimiter),
    getDataTaktAttributes: getDataTaktAttributesFunction({
      taktIdContext,
      taktIdPrefix,
      taktId: dataTaktId,
      taktValue
    }),
    parentTaktId: taktFeatureChain[(taktFeatureChain === null || taktFeatureChain === void 0 ? void 0 : taktFeatureChain.length) - 1]
  };
};

/**
 * TaktIdContext consumer that vends a function: (contextValue) => (Children)
 */
const TaktIdConsumer = ({
  children,
  taktId,
  fallbackId,
  taktValue,
  taktIdPrefix
}) => /*#__PURE__*/React__default.createElement(TaktIdContext.Consumer, null, taktIdContext => {
  const {
    dataTaktId,
    unprefixedTaktId,
    dataTaktValue,
    dataTaktFeature,
    getDataTaktAttributes
  } = getTaktIdParams({
    taktIdContext,
    taktId,
    fallbackId,
    taktValue,
    taktIdPrefix
  });

  // if there is no parent chain, do not append anything or return anything
  if (!dataTaktFeature) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children({
      dataTaktId,
      dataTaktValue,
      getDataTaktAttributes
    }));
  }
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: unprefixedTaktId,
    taktIdPrefix: taktIdPrefix,
    taktValue: dataTaktValue
  }, children({
    dataTaktId,
    dataTaktValue,
    dataTaktFeature,
    getDataTaktAttributes
  }));
});

/**
 * React hook to append an taktId to the context
 * without modifying the existing context value.
 *
 * If no context exists, this will return undefined.
 *
 * @param taktId: string - value returned as id if context exists
 * @param fallbackId: string - value returned as id if taktId is undefined and useFallbacks is true
 * @param taktValue: Record<string, unknown> - object appended to the context.taktValues and used as the data-takt-value
 * @param taktIdPrefix: string - a string that prefixes all lower level taktIds
 *
 * @returns params: TaktIdParams - object containing:
 *   - dataTaktId?: string - taktId value to be used. May be `undefined` if no context existed above.
 *   - dataTaktFeature?: string - delimited chain of Ids from all TaktIdProviders & TaktIdConsumers
 *     above this hook call in the hierarchy
 *   - getDataTaktAttributes: GetDataTaktAttributes
 */
const useTaktId = ({
  taktId,
  fallbackId,
  taktValue,
  taktIdPrefix
}) => {
  const taktIdContext = useContext(TaktIdContext);
  return getTaktIdParams({
    taktIdContext,
    taktId,
    fallbackId,
    taktValue,
    taktIdPrefix
  });
};

/**
 * For non-exported components already have taktId prop but need a new fallback id
 * This should only be used on non-exported components, otherwise we expose the fallbackId prop.
 */
function withTaktFallbackId(Component) {
  const ComponentWithTaktFallbackId = props => {
    const {
      taktId,
      taktFallbackId,
      taktValue,
      ...restProps
    } = props;
    // remove the prefix so we can set it through the `useTaktId` calls inside the components
    const {
      unprefixedTaktId,
      dataTaktValue
    } = useTaktId({
      taktId,
      fallbackId: taktFallbackId,
      taktValue
    });

    // https://github.com/Microsoft/TypeScript/issues/28938#issuecomment-450636046
    return /*#__PURE__*/React__default.createElement(Component, _extends({
      taktId: unprefixedTaktId,
      taktValue: dataTaktValue
    }, restProps));
  };
  return ComponentWithTaktFallbackId;
}

export { TaktIdProvider as T, TaktIdConsumer as a, TaktIdContext as b, useTaktId as u, withTaktFallbackId as w };
//# sourceMappingURL=TaktIdContext-C6vdL-6Z.js.map
